import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, EffectNotification, ofType, OnRunEffects } from "@ngrx/effects";
import { EMPTY, Observable } from "rxjs";
import { concatMap, filter, switchMap } from "rxjs/operators";
import { ClassroomConfig, PCP_CLASSROOM_SYNC_CONFIG } from "../../public/injection-tokens";
import { LessonCommandFacade } from "../../public/commands/lesson-command.facade";
import { ClassroomEffectsHelperService } from "../../public/services/classroom-effects-helper.service";
import * as ClassroomEvents from "../../public/classroom-events.actions";

/**
 * SignalR-direct emitter effects: relay user interactions from one role to another via
 * `lessonHub.send*` calls. Distinct from Y.Doc state writes (those go through
 * `ClassroomFacade.setSlide(...)` etc. at the dispatch sites).
 *
 * After Part 1 (Plan 06) inlined the video / rubber-element / perception broadcasts and the
 * beamer→teacher preview-navigation intents directly into `SlidePreviewFacade` (gated by
 * `ClassroomEmitGateService`), and Part 2 moved the beamer toolbar undo/redo/navigate-to-parent
 * relay into `ToolbarSignalrSyncEffects` (`shared/classroom/toolbar/data-access`), only the
 * beamer hotspot-click relay remains here — a genuine multi-listener action that also drives
 * local fan-out.
 */
@Injectable()
export class ClassroomSignalrEmitterEffects implements OnRunEffects {

    private readonly classroomHelper = inject(ClassroomEffectsHelperService);
    private readonly config = inject<ClassroomConfig>(PCP_CLASSROOM_SYNC_CONFIG);
    private readonly actions$ = inject(Actions);
    private readonly lessonCmd = inject(LessonCommandFacade);

    /**
     * Gate the SignalR sends on having an active lesson + hub connection. Without a
     * connected hub the `lessonHub.send*` calls would either error or silently drop.
     */
    ngrxOnRunEffects(
        resolvedEffects$: Observable<EffectNotification>
    ): Observable<EffectNotification> {
        if (!this.config.isEmitter) {
            return EMPTY;
        }

        return this.classroomHelper.hasLessonIdAndIsConnected$.pipe(
            switchMap(() => resolvedEffects$)
        );
    }

    /**
     * User has activated hotspot in primary slide.
     * If the classroom is configured to emit hotspot clicks (beamer),
     * send the clicked hotspot to the teacher so that the teacher can lazy-load additional information
     * and activate it in the teacher UI (which will send back the dynamic slide to the beamer).
     */
    readonly sendHotspotClicksToTeacher$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ClassroomEvents.hotspotActivated),
                filter((_) => this.config.emitHotspotClicks === true),
                concatMap((action) =>
                    this.lessonCmd.sendIntent.activateHotspot(
                        action.hotspot,
                        action.slide,
                        1,
                    )
                )
            ),
        { dispatch: false }
    );
}
